import { createSlice } from "@reduxjs/toolkit";
import {
  trackClick,
  sendClickBatch,
  getClickAnalytics,
  getInefficiencies,
  getPageSummary,
  getUserJourney,
  getAnalyticsSummary,
} from "../thunks/analyticsThunk";

const initialState = {
  // Click batching
  clickBatch: [],
  lastSent: null,
  batchSize: 10, // Send after 10 clicks
  batchTimeout: 30000, // or 30 seconds

  // Analytics data
  analytics: null,
  inefficiencies: null,
  pageSummary: null,
  userJourney: null,
  summary: null,

  // UI state
  loading: false,
  error: null,
  lastError: null,
};

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    clearBatch: (state) => {
      state.clickBatch = [];
      state.lastSent = Date.now();
    },
    setBatchSize: (state, action) => {
      state.batchSize = action.payload;
    },
    setBatchTimeout: (state, action) => {
      state.batchTimeout = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAnalyticsData: (state) => {
      state.analytics = null;
      state.inefficiencies = null;
      state.pageSummary = null;
      state.userJourney = null;
      state.summary = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Track Click
      .addCase(trackClick.fulfilled, (state, action) => {
        if (action.payload.clickData) {
          // Add to batch
          state.clickBatch.push(action.payload.clickData);

          // Log for debugging
          console.log(`[Analytics Slice] Click added to batch. Batch size: ${state.clickBatch.length}/${state.batchSize}`);
        }
      })
      .addCase(trackClick.rejected, (state, action) => {
        state.lastError = action.payload || "Failed to track click";
        console.error(`[Analytics Slice] Error tracking click:`, state.lastError);
      })

      // Send Click Batch
      .addCase(sendClickBatch.pending, (state) => {
        console.log(`[Analytics Slice] Sending batch of ${state.clickBatch.length} clicks...`);
      })
      .addCase(sendClickBatch.fulfilled, (state, action) => {
        if (action.payload.sent) {
          console.log(`[Analytics Slice] Batch sent successfully: ${action.payload.batchSize} clicks`);
          state.clickBatch = [];
          state.lastSent = Date.now();
        }
      })
      .addCase(sendClickBatch.rejected, (state, action) => {
        state.error = action.payload || "Failed to send click batch";
        state.lastError = state.error;
        console.error(`[Analytics Slice] Error sending batch:`, state.error);
      })

      // Get Click Analytics
      .addCase(getClickAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClickAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload;
        state.loading = false;
        console.log(`[Analytics Slice] Analytics loaded:`, {
          totalClicks: action.payload.totalClicks,
          uniqueUsers: action.payload.uniqueUsers,
          analysisCount: action.payload.analysis?.length,
        });
      })
      .addCase(getClickAnalytics.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch analytics";
        state.lastError = state.error;
        state.loading = false;
        console.error(`[Analytics Slice] Error fetching analytics:`, state.error);
      })

      // Get Inefficiencies
      .addCase(getInefficiencies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInefficiencies.fulfilled, (state, action) => {
        state.inefficiencies = action.payload.inefficiencies;
        state.loading = false;
        console.log(`[Analytics Slice] Inefficiencies loaded: ${action.payload.inefficiencies?.length} found`);
      })
      .addCase(getInefficiencies.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch inefficiencies";
        state.lastError = state.error;
        state.loading = false;
        console.error(`[Analytics Slice] Error fetching inefficiencies:`, state.error);
      })

      // Get Page Summary
      .addCase(getPageSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPageSummary.fulfilled, (state, action) => {
        state.pageSummary = action.payload;
        state.loading = false;
        console.log(`[Analytics Slice] Page summary loaded for ${action.payload.path}`);
      })
      .addCase(getPageSummary.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch page summary";
        state.lastError = state.error;
        state.loading = false;
        console.error(`[Analytics Slice] Error fetching page summary:`, state.error);
      })

      // Get User Journey
      .addCase(getUserJourney.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserJourney.fulfilled, (state, action) => {
        state.userJourney = action.payload;
        state.loading = false;
        console.log(`[Analytics Slice] User journey loaded: ${action.payload.clickCount} clicks`);
      })
      .addCase(getUserJourney.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch user journey";
        state.lastError = state.error;
        state.loading = false;
        console.error(`[Analytics Slice] Error fetching user journey:`, state.error);
      })

      // Get Analytics Summary
      .addCase(getAnalyticsSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnalyticsSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
        state.loading = false;
        console.log(`[Analytics Slice] Summary loaded:`, {
          daysBack: action.payload.daysBack,
          totalClicks: action.payload.totalClicks,
          uniqueUsers: action.payload.uniqueUsers,
        });
      })
      .addCase(getAnalyticsSummary.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch analytics summary";
        state.lastError = state.error;
        state.loading = false;
        console.error(`[Analytics Slice] Error fetching summary:`, state.error);
      });
  },
});

export const {
  clearBatch,
  setBatchSize,
  setBatchTimeout,
  clearError,
  clearAnalyticsData,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;
